import { createContext, useContext } from "react";

/**
 * EXPERIMENT -- popsicle-stick paper puppets.
 *
 * Provided by the show stage around one puppet. When present, PersonaImage
 * draws a wooden stick BEHIND the sprite, attached at the middle of the drawn
 * body and running down past the frame, and can give the sprite a white
 * cut-paper edge.
 *
 * The stick's reach below the frame is `--puppet-stick-below`, in the image's
 * own (pre-transform) pixels. The stage writes it every frame so the stick
 * always ends on the puppet's RESTING foot line: hidden while the puppet stands
 * there, showing the moment anything lifts it.
 */
export interface PuppetStickStyle {
  stick: boolean;
  /**
   * Stick width in stage pixels -- the same for every puppet whatever its
   * size; the image divides out its own scale. Omitted: 7% of the frame.
   */
  stickPx?: number;
  /** Width of the white paper margin around the sprite, in image pixels. 0 = none. */
  edgePx: number;
}

export const PuppetStickContext = createContext<PuppetStickStyle | null>(null);
export const usePuppetStick = () => useContext(PuppetStickContext);

const PAPER = "#fbf8ef";

/**
 * Hard offset copies make a solid margin around the sprite's alpha. None
 * downward: a waist-up puppet's flat cut edge sits on the ledge, and a white
 * strip under it would say the paper ends there.
 */
export function paperEdgeFilter(px: number): string | undefined {
  if (!(px > 0)) return undefined;
  const e = px.toFixed(2);
  return [
    `drop-shadow(${e}px 0 0 ${PAPER})`,
    `drop-shadow(-${e}px 0 0 ${PAPER})`,
    `drop-shadow(0 -${e}px 0 ${PAPER})`,
  ].join(" ");
}
